import { NO_REACH } from '@alluzo/shared';
import i18next from 'i18next';
import type {
  ApiError,
  AuthDoorsResponse,
  AttachmentCommitResponse,
  AttachmentCreateRequest,
  AttachmentCreateResponse,
  AttachmentDeleteResponse,
  AttachmentListResponse,
  BusinessCreateRequest,
  BusinessCreateResponse,
  BusinessDetailResponse,
  BusinessSetupConversationResponse,
  BusinessListResponse,
  BusinessPatchRequest,
  CapabilityCatalogueResponse,
  ComposeRequest,
  EntryBatchItem,
  EntryBatchResponse,
  EntryDeleteResponse,
  EntryProblem,
  EntryListResponse,
  EntryLinesWrite,
  EntryValues,
  EntryWriteResponse,
  GrantPatchRequest,
  GrantPatchResponse,
  GrantRefollowResponse,
  GrantRevokeResponse,
  HealthResponse,
  InviteAcceptRequest,
  InviteAcceptResponse,
  InviteCreateRequest,
  InviteCreateResponse,
  InvitePreviewResponse,
  InviteRevokeResponse,
  JobApplyRequest,
  JobApplyResponse,
  JobArchiveResponse,
  JobCreateRequest,
  JobImpactRequest,
  JobImpactResponse,
  JobListResponse,
  JobPatchRequest,
  JobResponse,
  LinkCreateRequest,
  LinkListResponse,
  LinkPatchRequest,
  LinkWriteResponse,
  MeResponse,
  MemberGrantListResponse,
  MemberListResponse,
  MemberPatchRequest,
  MemberPatchResponse,
  PrimaryRequestCreateResponse,
  PrimaryRequestDismissResponse,
  PrimaryRequestListResponse,
  MemberRemoveResponse,
  OnboardingApplyRequest,
  OnboardingApplyResponse,
  RecordBlankSpec,
  RecordCreateRequest,
  RecordCreateResponse,
  RecordGrantListResponse,
  RecordListResponse,
  RecordSchemaResponse,
  RecordStructureRequest,
  RecordStructureResponse,
  RecordStructureSaveResponse,
  SearchResponse,
} from '@alluzo/shared';
import { REGISTRY_VERSION, SYNC_BYTES_HEADER } from '@alluzo/shared';
/**
 * `expo/fetch` by name, not the global.
 *
 * Expo SDK 57 installs this same implementation as the global `fetch` on
 * Android and iOS, so on native the two are one thing. Importing it by name
 * says which one we mean, keeps web on the same implementation as native, and
 * survives `EXPO_PUBLIC_USE_RN_FETCH=1` — the flag that puts React Native's own
 * fetch back as the global and would otherwise change what this file does.
 *
 * https://docs.expo.dev/versions/v57.0.0/sdk/expo/
 *
 * Reached through `@/lib/client-fetch`, which adds the header that says which
 * app and build is calling.
 */
import { fetch } from '@/lib/client-fetch';
import { isRecordId } from '@/lib/record-id';
import type { SyncPullRequest, SyncPullResponse } from '@/lib/sync-types';

/** Set EXPO_PUBLIC_API_URL to reach a server that is not on this machine. */
const baseUrl = process.env.EXPO_PUBLIC_API_URL ?? 'http://localhost:4000';

/** Must match `TENANT_HEADER` in apps/server/src/tenant-context.ts. */
export const TENANT_HEADER = 'x-alluzo-tenant';

/**
 * A `/v1` call the server refused. Carries the status and the server's own
 * error code, because "what to tell the person" depends on both.
 *
 * `problems` carries the field-level list a refused entry write answers with.
 * The server's own sentence says "see problems for each field", so dropping
 * them would leave that sentence pointing at nothing.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly problems: readonly EntryProblem[];

  constructor(
    status: number,
    code: string | null,
    message: string,
    problems: readonly EntryProblem[] = [],
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.problems = problems;
  }
}

/** One problem row, or null for anything that is not one. */
const asProblem = (value: unknown): EntryProblem | null => {
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.code !== 'string' || typeof row.message !== 'string') return null;
  return {
    code: row.code,
    fieldId: typeof row.fieldId === 'string' ? row.fieldId : null,
    message: row.message,
  };
};

/** Reads the `ApiError` envelope, falling back to the bare status. */
const refusalOf = async (response: Response): Promise<ApiRequestError> => {
  try {
    const body = (await response.json()) as Partial<ApiError> & { error?: { problems?: unknown } };
    const error = body.error;
    if (error !== undefined) {
      const raw = Array.isArray(error.problems) ? error.problems : [];
      const problems = raw.map(asProblem).filter((row): row is EntryProblem => row !== null);
      return new ApiRequestError(response.status, error.code, error.message, problems);
    }
  } catch {
    /* Not JSON. The status is all we have. */
  }
  return new ApiRequestError(response.status, null, `HTTP ${response.status}`);
};

export const checkHealth = async (): Promise<HealthResponse> => {
  const response = await fetch(`${baseUrl}/v1/health`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return (await response.json()) as HealthResponse;
};

/**
 * GET /v1/me — who is signed in, and which businesses they may open.
 *
 * On native the platform does not attach the session cookie: it lives in
 * expo-secure-store, and the Better Auth client hands it over on request. The
 * caller passes it in, which is what keeps this module free of a dependency on
 * `@/lib/auth` — that module already depends on this one.
 */
export const fetchMe = async (cookie: string): Promise<MeResponse> => {
  const response = await fetch(`${baseUrl}/v1/me`, {
    headers: { accept: 'application/json', 'accept-language': i18next.language, cookie },
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as MeResponse;
};

/**
 * POST /v1/me/deletion-request — queues this account for erasure.
 *
 * The endpoint records the ask and nothing more: the erasure itself is an
 * operator-run script on the server, so a stolen cookie can destroy nothing
 * through here. The device does not wait for that run — once this resolves,
 * the caller signs out and wipes every local trace right away.
 */
export const requestAccountDeletion = async (cookie: string): Promise<void> => {
  const response = await fetch(`${baseUrl}/v1/me/deletion-request`, {
    method: 'POST',
    headers: { accept: 'application/json', 'accept-language': i18next.language, cookie },
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
};

/**
 * PUT /v1/me/photo — the profile photo, as its bytes. The server re-encodes it
 * into the asset store and answers the address `auth.user.image` now holds.
 */
export const uploadProfilePhoto = async (cookie: string, photo: Blob): Promise<string> => {
  const response = await fetch(`${baseUrl}/v1/me/photo`, {
    method: 'PUT',
    headers: { accept: 'application/json', 'accept-language': i18next.language, 'content-type': photo.type, cookie },
    body: photo,
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return ((await response.json()) as { image: string }).image;
};

/** DELETE /v1/me/photo — no photo; the avatar falls back to initials. */
export const removeProfilePhoto = async (cookie: string): Promise<void> => {
  const response = await fetch(`${baseUrl}/v1/me/photo`, {
    method: 'DELETE',
    headers: { accept: 'application/json', 'accept-language': i18next.language, cookie },
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
};

/** Where anyone who shares a business with this person reads their photo. A 404 means initials. */
export const personPhotoUrl = (userId: string): string => `${baseUrl}/v1/people/${userId}/photo`;

/**
 * Where an address the server stored as its own path is read: an uploaded
 * logo is kept as `/v1/businesses/<id>/logo?v=…`. Read with the session
 * cookie, as a person's photo is.
 */
export const apiAddressOf = (path: string): string => `${baseUrl}${path}`;

export const applyOnboarding = async (
  cookie: string,
  body: OnboardingApplyRequest,
): Promise<OnboardingApplyResponse> => {
  const response = await fetch(`${baseUrl}/v1/onboarding/apply`, {
    method: 'POST',
    headers: { accept: 'application/json', 'accept-language': i18next.language, 'content-type': 'application/json', cookie },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as OnboardingApplyResponse;
};

/**
 * The zone this device is in, or null when the engine will not say.
 *
 * `Intl` carries the whole app's money and date formatting, so it is present in
 * practice. The guard is here because the one thing that must not happen is
 * home refusing to load over a timezone string: the server answers without it,
 * in UTC.
 */
const deviceZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
};

/**
 * GET /v1/records — the home screen's whole payload.
 *
 * The device's zone rides along, because the server has no tenant timezone
 * column and "today" is a question about where the shop is, not where the
 * server is.
 */
export const fetchRecords = async (cookie: string, tenantId: string): Promise<RecordListResponse> => {
  const zone = deviceZone();
  const query = zone === null ? '' : `?tz=${encodeURIComponent(zone)}`;
  const response = await fetch(`${baseUrl}/v1/records${query}`, {
    headers: { accept: 'application/json', 'accept-language': i18next.language, cookie, [TENANT_HEADER]: tenantId },
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as RecordListResponse;
};

/** POST /v1/records — one more blank record or list, into the business that already exists. */
export const createRecord = async (
  cookie: string,
  tenantId: string,
  request: RecordCreateRequest,
): Promise<RecordCreateResponse> => {
  const response = await fetch(`${baseUrl}/v1/records`, {
    method: 'POST',
    headers: { accept: 'application/json', 'accept-language': i18next.language, 'content-type': 'application/json', cookie, [TENANT_HEADER]: tenantId },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as RecordCreateResponse;
};

/**
 * POST /v1/records — one record nobody's template made.
 *
 * The same route as `createRecord`, and on purpose: the server ends both in
 * one apply, so there is no second path here either. The answer carries one
 * record row either way, a list or a record.
 */
export const createBlankRecord = async (
  cookie: string,
  tenantId: string,
  blank: RecordBlankSpec,
): Promise<RecordCreateResponse> => {
  return createRecord(cookie, tenantId, { blank });
};

/* ------------------------------------------------------------------ entries */

/** Everything under /v1 that needs a person and a business sends these three. */
const tenantHeaders = (cookie: string, tenantId: string): Record<string, string> => ({
  accept: 'application/json',
      'accept-language': i18next.language,
  cookie,
  [TENANT_HEADER]: tenantId,
});

export const readOrThrow = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as T;
};

/**
 * GET /v1/records/:recordId/schema
 *
 * The registry version goes with it, and this is the only place it is sent.
 * A binary that can draw a stepper says so; one that cannot is answered in
 * primitives it definitely holds. Section 7.2.
 *
 * An id that is not a record's is refused here and never sent: a route that
 * lost its id asked for `/v1/records/undefined/schema` and showed the server's
 * 400. What it says instead is what the app says of any page that is not there.
 */
export const fetchRecordSchema = async (
  cookie: string,
  tenantId: string,
  recordId: string,
): Promise<RecordSchemaResponse> => {
  if (!isRecordId(recordId)) throw new Error(i18next.t('route.not_found.title'));
  return readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/schema?registry_version=${REGISTRY_VERSION}`, {
      headers: tenantHeaders(cookie, tenantId),
    }),
  );
};

/**
 * GET /v1/search
 *
 * Every record of one business, searched on the server. The phone's own index
 * only reaches what it still holds; this reaches the rest.
 */
export const fetchServerSearch = async (
  cookie: string,
  tenantId: string,
  query: string,
  limit: number,
  cursor?: string,
): Promise<SearchResponse> => {
  const search = new URLSearchParams({ q: query, limit: String(limit) });
  if (cursor !== undefined && cursor !== '') search.set('cursor', cursor);
  return readOrThrow(
    await fetch(`${baseUrl}/v1/search?${search.toString()}`, { headers: tenantHeaders(cookie, tenantId) }),
  );
};

export const fetchEntries = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  options: { query?: string; limit?: number; cursor?: string; before?: string } = {},
): Promise<EntryListResponse> => {
  const search = new URLSearchParams();
  if (options.query !== undefined && options.query !== '') search.set('q', options.query);
  if (options.limit !== undefined) search.set('limit', String(options.limit));
  // Where the last page stopped. Opaque here: it is written and read by the
  // route, and this only hands it back.
  if (options.cursor !== undefined && options.cursor !== '') search.set('cursor', options.cursor);
  // The oldest day this device holds. The first page of history starts below it.
  if (options.before !== undefined && options.before !== '') search.set('before', options.before);
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`;
  return readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/entries${suffix}`, {
      headers: tenantHeaders(cookie, tenantId),
    }),
  );
};

/**
 * POST /v1/records/:recordId/entries
 *
 * The device mints the entry id, and the outbox op's id rides along as
 * clientOpId: the server is idempotent on entryId, so a retried push of the
 * same op lands once. The shared request type gains these as optional fields
 * server-side; until it lands there, the body is widened locally here.
 */
export const createEntry = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  values: EntryValues,
  ids: { entryId?: string; clientOpId?: string } = {},
  /** The entry's lines, in the same write (Decision 5 of the lines plan). */
  lines?: EntryLinesWrite,
): Promise<EntryWriteResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/entries`, {
      method: 'POST',
      headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
      body: JSON.stringify({ values, ...ids, ...(lines === undefined ? {} : { lines }) }),
    }),
  );

/**
 * POST /v1/records/:recordId/entries/batch
 *
 * The same write as `createEntry`, up to `ENTRY_BATCH_MAX` of them in one
 * transaction. A sheet of attendance marks is fifty rows saved at once, and
 * fifty round trips over a shop's wifi is the difference between a save that
 * feels instant and one somebody watches.
 *
 * Every id is the device's own, so a retried batch is a replay and never a
 * second sheet of marks. The failure mapping is `createEntry`'s, unchanged: a
 * refusal throws an `ApiRequestError` carrying the status the push branches on.
 * The batch is all or nothing, so a 4xx means NOTHING landed — the push sends
 * the group again one at a time, which is where the bad row gets found.
 */
export const createEntries = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  entries: readonly EntryBatchItem[],
): Promise<EntryWriteResponse[]> => {
  const written = await readOrThrow<EntryBatchResponse>(
    await fetch(`${baseUrl}/v1/records/${recordId}/entries/batch`, {
      method: 'POST',
      headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
      body: JSON.stringify({ entries }),
    }),
  );
  return written.entries;
};

/**
 * PATCH /v1/records/:recordId/entries/:entryId
 *
 * A merge: only the keys sent move, and the server revalidates the whole.
 * `clientOpId` is the outbox op behind it, since an edit queues now — validated
 * server-side and carried no further, so a device log and a support ticket can
 * name the same write. Absent from the screen that edits an entry directly,
 * which has no op because it waits for the answer.
 */
export const updateEntry = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  entryId: string,
  values: EntryValues,
  /** `baseVersion`: the version the edit was read at; a later one refuses it (409 ENTRY_MOVED). */
  ids: { clientOpId?: string; baseVersion?: number } = {},
  /**
   * The full set of each `lines` field the edit names. A field left out keeps
   * its stored lines (Decision 5 of the lines plan).
   */
  lines?: EntryLinesWrite,
): Promise<EntryWriteResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/entries/${entryId}`, {
      method: 'PATCH',
      headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
      // entryId rides in the body as well as the path, matching the widened
      // write contract.
      body: JSON.stringify({ values, entryId, ...ids, ...(lines === undefined ? {} : { lines }) }),
    }),
  );

export const deleteEntry = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  entryId: string,
): Promise<EntryDeleteResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/entries/${entryId}`, {
      method: 'DELETE',
      headers: tenantHeaders(cookie, tenantId),
    }),
  );

export const apiBaseUrl = baseUrl;

/* ------------------------------------------------------------------- doors */

/**
 * GET /v1/auth/doors — which ways in this deployment can open.
 *
 * The one call the app makes with NO session, and the only one that may fail
 * without stopping the person: a sign-up screen that cannot reach the server
 * must still offer the email door, because that is the one door whose failure
 * mode is a sentence the person can read rather than a blank screen.
 *
 * So a failure here answers the floor rather than throwing. `bun run server`
 * being down is exactly the case, and the email form's own error already says
 * so in words when they submit it.
 */
export const fetchAuthDoors = async (): Promise<AuthDoorsResponse> => {
  try {
    return await readOrThrow<AuthDoorsResponse>(
      await fetch(`${baseUrl}/v1/auth/doors`, { headers: { accept: 'application/json' } }),
    );
  } catch {
    return { doors: ['email'], reach: NO_REACH, emailCodeDelivers: false };
  }
};

/* -------------------------------------------------------------------- sync */

/**
 * POST /v1/sync/pull — one page of everything that changed after `since`.
 *
 * The replication read: entry versions in server_seq order, the catalog, the
 * schemas that changed against `schemaVersions`, and the next cursor. The
 * sync engine loops it while the server says there is more.
 */
export const fetchSyncPull = async (
  cookie: string,
  tenantId: string,
  body: SyncPullRequest,
): Promise<{ page: SyncPullResponse; bytes: number }> => {
  const response = await fetch(`${baseUrl}/v1/sync/pull`, {
    method: 'POST',
    headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const page = await readOrThrow<SyncPullResponse>(response);
  // The size as sent, for the download bar. The server's own header, because
  // Android drops content-length when it unzips; 0 from a server too old to
  // send it, which leaves the bar right and the sizes at 0.
  const sent = Number(response.headers.get(SYNC_BYTES_HEADER) ?? '');
  return { page, bytes: Number.isFinite(sent) && sent > 0 ? sent : 0 };
};

/* ---------------------------------------------------------------- structure */

/**
 * GET /v1/records/:recordId/structure — what the customize screen reads.
 *
 * Not the schema. A schema is what a form renders, so a switched-off field is
 * absent from it; the editor has to see that field to switch it back on. No
 * registry_version rides along either: the editor shows the component that was
 * asked for, not the one this device can draw.
 */
export const fetchRecordStructure = async (
  cookie: string,
  tenantId: string,
  recordId: string,
): Promise<RecordStructureResponse> => {
  const response = await fetch(`${baseUrl}/v1/records/${recordId}/structure`, {
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as RecordStructureResponse;
};

/**
 * PATCH /v1/records/:recordId/structure — the whole draft, in one save.
 *
 * The server answers with the structure as it now stands, so the screen renders
 * truth rather than its own optimism, AND with the schema the entry form will
 * draw, so the device's mirror is right before the next pull rather than 45
 * seconds after it. A 409 means somebody else changed the record while this
 * draft was open, and the screen reloads rather than merging.
 *
 * `registry_version` for the same reason the schema read sends it: the schema
 * that comes back is resolved for what THIS binary can draw, and caching one
 * resolved for a newer client would mirror a component this device cannot
 * render.
 */
export const saveRecordStructure = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  body: RecordStructureRequest,
): Promise<RecordStructureSaveResponse> => {
  const response = await fetch(
    `${baseUrl}/v1/records/${recordId}/structure?registry_version=${REGISTRY_VERSION}`,
    {
      method: 'PATCH',
      headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as RecordStructureSaveResponse;
};

/* --------------------------------------------------------------------- links */

/**
 * The links API. Section 5.7.
 *
 * Online only, exactly as structure editing is: a rule is a piece of the
 * record's shape, and a shape that two devices edited offline is a merge
 * nobody can review. What a link WRITES is offline-first; the rule itself is
 * not.
 */
export const fetchLinks = async (
  cookie: string,
  tenantId: string,
  recordId: string,
): Promise<LinkListResponse> => {
  const response = await fetch(`${baseUrl}/v1/records/${recordId}/links`, {
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) throw await refusalOf(response);
  return (await response.json()) as LinkListResponse;
};

export const createLink = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  body: LinkCreateRequest,
): Promise<LinkWriteResponse> => {
  const response = await fetch(`${baseUrl}/v1/records/${recordId}/links`, {
    method: 'POST',
    headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await refusalOf(response);
  return (await response.json()) as LinkWriteResponse;
};

/** The pause switch, and every other part of the rule. */
export const patchLink = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  linkId: string,
  body: LinkPatchRequest,
): Promise<LinkWriteResponse> => {
  const response = await fetch(`${baseUrl}/v1/records/${recordId}/links/${linkId}`, {
    method: 'PATCH',
    headers: { ...tenantHeaders(cookie, tenantId), 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await refusalOf(response);
  return (await response.json()) as LinkWriteResponse;
};

/** Retire a link. Every row it wrote stays exactly where it is. */
export const deleteLink = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  linkId: string,
): Promise<void> => {
  const response = await fetch(`${baseUrl}/v1/records/${recordId}/links/${linkId}`, {
    method: 'DELETE',
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) throw await refusalOf(response);
};

/**
 * DELETE /v1/records/:recordId: a record or a list leaves every page. The
 * answer names what went with it, which the agent's confirm does not show, so
 * this call does not read it. A refusal is thrown as an `ApiRequestError`, as
 * the other calls here throw. The web's copy is `features/record/record-api.ts`.
 */
export const deleteRecord = async (cookie: string, tenantId: string, recordId: string): Promise<void> => {
  const response = await fetch(`${baseUrl}/v1/records/${recordId}`, {
    method: 'DELETE',
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) throw await refusalOf(response);
};

/* --------------------------------------------------------------------- team */

/**
 * The team API: businesses, members, invitations, jobs, grants. Section 11.
 *
 * Three header rules, and they are not a matter of taste — the server enforces
 * each one:
 *
 *   PATH TENANT   `/v1/businesses/:id/...` reads the business off the path and
 *                 checks it against the caller's memberships. Sending the
 *                 header there would let two places disagree, so these calls
 *                 take `businessId` and send no tenant header at all.
 *   HEADER TENANT `/v1/jobs/:id/...`, `/v1/records/:recordId/grants/...` and
 *                 `DELETE /v1/invites/:id` name a row whose business is not in
 *                 the path, so they MUST carry TENANT_HEADER. The server
 *                 answers 400 TENANT_REQUIRED without it.
 *   NO TENANT     `POST /v1/businesses` makes the tenant it is about, and
 *                 `POST /v1/invites/accept` names its business through the
 *                 token. Both answer 400 TENANT_HEADER_NOT_ALLOWED if a header
 *                 arrives, so neither may be given one.
 *
 * `GET /v1/capabilities` and `GET /v1/businesses` are about the person rather
 * than a business, and take no tenant either way.
 *
 * None of this is cached on the device. A management screen is read on open:
 * the answers are small, they are about who may do what, and a stale copy of
 * that is worse than a spinner.
 */

/** Everything under /v1 that needs a person and no business sends these two. */
export const personHeaders = (cookie: string): Record<string, string> => ({
  accept: 'application/json',
      'accept-language': i18next.language,
  cookie,
});

const jsonHeaders = (headers: Record<string, string>): Record<string, string> => ({
  ...headers,
  'content-type': 'application/json',
});

/**
 * GET /v1/capabilities — the vocabulary the role editor renders from.
 *
 * Both lists are seeded by migration and change only by migration, so the
 * server holds its answer for the life of its process and answers
 * `cache-control: private, max-age=...`. The device reads it once per app run;
 * see `use-capabilities.ts`.
 */
export const fetchCapabilities = async (cookie: string): Promise<CapabilityCatalogueResponse> =>
  readOrThrow(await fetch(`${baseUrl}/v1/capabilities`, { headers: personHeaders(cookie) }));

/* ------------------------------------------------------------- the business */

/** GET /v1/businesses — the same list /v1/me answers, served on its own. */
export const fetchBusinesses = async (cookie: string): Promise<BusinessListResponse> =>
  readOrThrow(await fetch(`${baseUrl}/v1/businesses`, { headers: personHeaders(cookie) }));

/**
 * POST /v1/businesses — a second business, from the same three screens.
 *
 * No tenant header: this route makes the tenant it is about. `clientKey` is
 * what makes a retried tap idempotent, so the caller must mint it ONCE per
 * attempt and re-send the same one — see `use-businesses.ts`.
 */
export const createBusiness = async (
  cookie: string,
  body: BusinessCreateRequest,
): Promise<BusinessCreateResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses`, {
      method: 'POST',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/**
 * POST /v1/onboarding/compose — the business the agent composed, made real.
 *
 * No tenant header, for the reason `createBusiness` gives: this route makes the
 * tenant it is about. `clientKey` is the same idempotency contract too — minted
 * once per proposal and re-sent on a retried tap, so a replay answers 200 with
 * the business the first call made. A 400 `COMPOSE_INVALID` carries every
 * problem the server found in `problems`, which `refusalOf` keeps.
 *
 * The three optional members are the business's first memory (decisions 1,
 * 3 and 13 of docs/plans/agent-business-memory.md): the understanding the
 * interview noted, the gather turns, and whether they were typed or spoken.
 * A body without them still makes a business.
 */
export const composeBusiness = async (cookie: string, body: ComposeRequest): Promise<OnboardingApplyResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/onboarding/compose`, {
      method: 'POST',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/** GET /v1/businesses/:id — one business and the three numbers it leads with. */
export const fetchBusinessDetail = async (
  cookie: string,
  businessId: string,
): Promise<BusinessDetailResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}`, { headers: personHeaders(cookie) }),
  );

/** GET /v1/businesses/:id/setup-conversation — the conversation the workspace was set up in, for people who may change its details. */
export const fetchSetupConversation = async (
  cookie: string,
  businessId: string,
): Promise<BusinessSetupConversationResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/setup-conversation`, { headers: personHeaders(cookie) }),
  );

/** PATCH /v1/businesses/:id — the name, the logo, the currency. */
export const patchBusiness = async (
  cookie: string,
  businessId: string,
  body: BusinessPatchRequest,
): Promise<BusinessDetailResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}`, {
      method: 'PATCH',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/**
 * PUT /v1/businesses/:id/logo — the business's logo, as its bytes (JPEG, PNG or
 * WebP; the blob's own type is the content type). The server fits it inside a
 * square, keeps it as a PNG in the asset store, and answers the business as it
 * now stands, its `logoUrl` the address the logo is read from
 * (`/v1/businesses/:id/logo?v=…`). The same people who may rename the business
 * may change its logo.
 */
export const uploadBusinessLogo = async (
  cookie: string,
  businessId: string,
  logo: Blob,
): Promise<BusinessDetailResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/logo`, {
      method: 'PUT',
      headers: { ...personHeaders(cookie), 'content-type': logo.type },
      body: logo,
    }),
  );

/** DELETE /v1/businesses/:id/logo — no logo; the mark falls back to the name's letters. */
export const removeBusinessLogo = async (cookie: string, businessId: string): Promise<BusinessDetailResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/logo`, {
      method: 'DELETE',
      headers: personHeaders(cookie),
    }),
  );

/* --------------------------------------------------------------- the people */

/** GET /v1/businesses/:id/members — the people and the invitations, in one read. */
export const fetchMembers = async (
  cookie: string,
  businessId: string,
): Promise<MemberListResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/members`, {
      headers: personHeaders(cookie),
    }),
  );

/** POST /v1/businesses/:id/invites — mint a code, with a level and maybe a job. */
export const createInvite = async (
  cookie: string,
  businessId: string,
  body: InviteCreateRequest,
): Promise<InviteCreateResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/invites`, {
      method: 'POST',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/**
 * POST /v1/invites/accept — claim a code.
 *
 * No tenant header, and not by omission: the token names its own business, so
 * the server answers 400 TENANT_HEADER_NOT_ALLOWED to a header here. The
 * caller does not know the business yet either, which is the point.
 */
export const acceptInvite = async (
  cookie: string,
  body: InviteAcceptRequest,
): Promise<InviteAcceptResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/invites/accept`, {
      method: 'POST',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/**
 * GET /v1/invites/preview?token= — what an invitation link offers, before
 * anybody signs in: the workspace, who invited, as what, how many records it
 * opens, and who it is for. No session and no tenant header. A dead link
 * answers 404 INVITE_INVALID with the one sentence every dead link says.
 */
export const previewInvite = async (token: string): Promise<InvitePreviewResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/invites/preview?${new URLSearchParams({ token }).toString()}`, {
      headers: { accept: 'application/json', 'accept-language': i18next.language },
    }),
  );

/**
 * DELETE /v1/invites/:id — withdraw a code nobody has claimed.
 *
 * HEADER tenant, not a path one: the business is nowhere in this path, and the
 * server refuses the call outright without TENANT_HEADER.
 */
export const revokeInvite = async (
  cookie: string,
  tenantId: string,
  inviteId: string,
): Promise<InviteRevokeResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/invites/${inviteId}`, {
      method: 'DELETE',
      headers: tenantHeaders(cookie, tenantId),
    }),
  );

/** PATCH /v1/businesses/:id/members/:userId — one person's level. */
export const patchMember = async (
  cookie: string,
  businessId: string,
  userId: string,
  body: MemberPatchRequest,
): Promise<MemberPatchResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/members/${userId}`, {
      method: 'PATCH',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/** POST /v1/businesses/:id/primary-requests — put a hand up for primary admin. */
export const requestPrimary = async (
  cookie: string,
  businessId: string,
): Promise<PrimaryRequestCreateResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/primary-requests`, {
      method: 'POST',
      headers: personHeaders(cookie),
    }),
  );

/** GET /v1/businesses/:id/primary-requests — the open asks, one per person. */
export const fetchPrimaryRequests = async (
  cookie: string,
  businessId: string,
): Promise<PrimaryRequestListResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/primary-requests`, {
      headers: personHeaders(cookie),
    }),
  );

/** DELETE /v1/businesses/:id/primary-requests/:userId — put an ask away. */
export const dismissPrimaryRequest = async (
  cookie: string,
  businessId: string,
  userId: string,
): Promise<PrimaryRequestDismissResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/primary-requests/${userId}`, {
      method: 'DELETE',
      headers: personHeaders(cookie),
    }),
  );

/**
 * DELETE /v1/businesses/:id/members/:userId — take the key back, keep the work.
 *
 * The answer counts what survives: every entry that person wrote stays in the
 * records with their name on it, so the confirmation can lead with that rather
 * than with what disappears.
 */
export const removeMember = async (
  cookie: string,
  businessId: string,
  userId: string,
): Promise<MemberRemoveResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/members/${userId}`, {
      method: 'DELETE',
      headers: personHeaders(cookie),
    }),
  );

/* ------------------------------------------------------------------ the job */

/** GET /v1/businesses/:id/jobs — the jobs we ship and this business's own. */
export const fetchJobs = async (cookie: string, businessId: string): Promise<JobListResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/jobs`, { headers: personHeaders(cookie) }),
  );

/** POST /v1/businesses/:id/jobs — a job in this business's own word. */
export const createJob = async (
  cookie: string,
  businessId: string,
  body: JobCreateRequest,
): Promise<JobResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/jobs`, {
      method: 'POST',
      headers: jsonHeaders(personHeaders(cookie)),
      body: JSON.stringify(body),
    }),
  );

/**
 * PATCH /v1/jobs/:id — rename it, or change what it may do.
 *
 * HEADER tenant: the business is not in this path. The answer's
 * `versionBumped` is the difference a screen must state before anybody saves —
 * false on a rename, which reaches nobody, and true on a spec change, which
 * reaches everybody who holds the job.
 */
export const patchJob = async (
  cookie: string,
  tenantId: string,
  jobId: string,
  body: JobPatchRequest,
): Promise<JobResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/jobs/${jobId}`, {
      method: 'PATCH',
      headers: jsonHeaders(tenantHeaders(cookie, tenantId)),
      body: JSON.stringify(body),
    }),
  );

/** DELETE /v1/jobs/:id — retire it. Every grant it wrote stays where it is. */
export const archiveJob = async (
  cookie: string,
  tenantId: string,
  jobId: string,
): Promise<JobArchiveResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/jobs/${jobId}`, {
      method: 'DELETE',
      headers: tenantHeaders(cookie, tenantId),
    }),
  );

/**
 * POST /v1/jobs/:id/impact — what applying this job WOULD do.
 *
 * READ ONLY. The server proves it: verify-jobs.ts counts app.record_grant
 * either side of a call. That is what makes it safe to call on every change of
 * the person picker, and why it is a separate function from `applyJob` rather
 * than a flag on it.
 */
export const fetchJobImpact = async (
  cookie: string,
  tenantId: string,
  jobId: string,
  body: JobImpactRequest,
): Promise<JobImpactResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/jobs/${jobId}/impact`, {
      method: 'POST',
      headers: jsonHeaders(tenantHeaders(cookie, tenantId)),
      body: JSON.stringify(body),
    }),
  );

/**
 * POST /v1/jobs/:id/apply — give the job to those people.
 *
 * The server runs the same impact statement first, inside the transaction, so
 * the set that is written is the set that was quoted. A person whose level
 * dropped between the preview and the tap is skipped, never an error.
 */
export const applyJob = async (
  cookie: string,
  tenantId: string,
  jobId: string,
  body: JobApplyRequest,
): Promise<JobApplyResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/jobs/${jobId}/apply`, {
      method: 'POST',
      headers: jsonHeaders(tenantHeaders(cookie, tenantId)),
      body: JSON.stringify(body),
    }),
  );

/* ---------------------------------------------------------------- the grant */

/** GET /v1/records/:recordId/grants — everybody who may open one record. */
export const fetchRecordGrants = async (
  cookie: string,
  tenantId: string,
  recordId: string,
): Promise<RecordGrantListResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/grants`, {
      headers: tenantHeaders(cookie, tenantId),
    }),
  );

/**
 * GET /v1/businesses/:id/members/:userId/grants — every record one person holds.
 *
 * PATH tenant, unlike the three retail calls below it: the business is right
 * there in the path, so this one sends no header.
 */
export const fetchMemberGrants = async (
  cookie: string,
  businessId: string,
  userId: string,
): Promise<MemberGrantListResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/businesses/${businessId}/members/${userId}/grants`, {
      headers: personHeaders(cookie),
    }),
  );

/**
 * PATCH /v1/records/:recordId/grants/:userId — change one grant by hand.
 *
 * The server sets `customised = true` unconditionally, because that is what a
 * hand edit MEANS. From then on `app.apply_role()` steps over this row until
 * somebody refollows it.
 */
export const patchGrant = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  userId: string,
  body: GrantPatchRequest,
): Promise<GrantPatchResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/grants/${userId}`, {
      method: 'PATCH',
      headers: jsonHeaders(tenantHeaders(cookie, tenantId)),
      body: JSON.stringify(body),
    }),
  );

/** DELETE /v1/records/:recordId/grants/:userId — take one record's key back. */
export const revokeGrant = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  userId: string,
): Promise<GrantRevokeResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/grants/${userId}`, {
      method: 'DELETE',
      headers: tenantHeaders(cookie, tenantId),
    }),
  );

/**
 * POST /v1/records/:recordId/grants/:userId/refollow — follow the job again.
 *
 * Clears `customised` and re-runs the job at its current version. Read the
 * server's own note before putting this on a screen: `app.apply_role()` is per
 * PERSON, so this re-shapes every non-customised grant that job gives that
 * person, not only the one named here.
 */
export const refollowGrant = async (
  cookie: string,
  tenantId: string,
  recordId: string,
  userId: string,
): Promise<GrantRefollowResponse> =>
  readOrThrow(
    await fetch(`${baseUrl}/v1/records/${recordId}/grants/${userId}/refollow`, {
      method: 'POST',
      headers: tenantHeaders(cookie, tenantId),
    }),
  );

/* -------------------------------------------------------------------------- */
/* Attachments                                                                */
/* -------------------------------------------------------------------------- */

/**
 * POST /v1/attachments — asks where to put one file.
 *
 * The answer carries a link the phone PUTs the bytes to, and the exact headers
 * the link was signed with. Send different headers and the store refuses it,
 * so `uploadHeaders` is passed through untouched rather than rebuilt.
 */
export const createAttachment = async (
  cookie: string,
  tenantId: string,
  body: AttachmentCreateRequest,
): Promise<AttachmentCreateResponse> => {
  const response = await fetch(`${baseUrl}/v1/attachments`, {
    method: 'POST',
    headers: jsonHeaders(tenantHeaders(cookie, tenantId)),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as AttachmentCreateResponse;
};

/**
 * POST /v1/attachments/:id/commit — claims an upload that has landed.
 *
 * This is where the server reads the bytes, removes the camera metadata, and
 * makes the thumbnail, so it is the slow call of the three and the one that
 * answers with something worth drawing.
 *
 * Safe to retry: a second commit of a file already committed answers the same
 * row rather than refusing.
 */
export const commitAttachment = async (
  cookie: string,
  tenantId: string,
  id: string,
): Promise<AttachmentCommitResponse> => {
  const response = await fetch(`${baseUrl}/v1/attachments/${id}/commit`, {
    method: 'POST',
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as AttachmentCommitResponse;
};

/**
 * GET /v1/attachments?ids=... — the rows an entry names, with fresh links.
 *
 * The links expire in minutes, so this is called when a screen opens and never
 * cached across one. An id the caller may not read is simply absent from the
 * answer rather than refused, because a form drawing four files must not fail
 * over the one its owner has since restricted.
 */
export const fetchAttachments = async (
  cookie: string,
  tenantId: string,
  ids: readonly string[],
): Promise<AttachmentListResponse> => {
  if (ids.length === 0) return { attachments: [] };
  const query = `?ids=${encodeURIComponent(ids.join(','))}`;
  const response = await fetch(`${baseUrl}/v1/attachments${query}`, {
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as AttachmentListResponse;
};

/** DELETE /v1/attachments/:id — removes one file, bytes and row. */
export const deleteAttachment = async (
  cookie: string,
  tenantId: string,
  id: string,
): Promise<AttachmentDeleteResponse> => {
  const response = await fetch(`${baseUrl}/v1/attachments/${id}`, {
    method: 'DELETE',
    headers: tenantHeaders(cookie, tenantId),
  });
  if (!response.ok) {
    throw await refusalOf(response);
  }
  return (await response.json()) as AttachmentDeleteResponse;
};
